import React, { useCallback, useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import styles from "./Hero.module.scss";
import { backgroundImg } from "src/assets/assetsExport";
import Icon from "src/components/Icon/Icon";

const PANEL_DIVIDER_BASE =
  "hidden md:block absolute w-1.5 inset-y-0 h-full transform skew-x-[-10deg] z-[55] pointer-events-none transition-opacity duration-[800ms] ease-[cubic-bezier(0.2,0.8,0.2,1)]";

const PANEL_DIVIDER_RIGHT = `${PANEL_DIVIDER_BASE} right-0`;
const PANEL_DIVIDER_LEFT = `${PANEL_DIVIDER_BASE} left-0`;

const DIVIDER_YELLOW = "bg-brand-yellow";
const DIVIDER_OBSIDIAN = "bg-text-obsidian";

const DIVIDER_VISIBLE = "opacity-80";
const DIVIDER_HIDDEN = "opacity-0";

type PanelId = "left" | "center" | "right";

const PANEL_META: Record<
  PanelId,
  { label: string; eyebrow: string; accent: string; divider: string }
> = {
  left: {
    label: "Buyers",
    eyebrow: "Sales & listings",
    accent: "border-brand-yellow",
    divider: "bg-brand-yellow shadow-[0_0_25px_rgba(255,196,0,0.6)]",
  },
  center: {
    label: "Renters",
    eyebrow: "Search live listings",
    accent: "border-text-obsidian",
    divider: "bg-text-obsidian shadow-[0_0_25px_rgba(0,0,0,0.25)]",
  },
  right: {
    label: "Landlords",
    eyebrow: "Lettings & returns",
    accent: "border-brand-yellow",
    divider: "",
  },
};

function panelDividerRightVisible(
  panel: PanelId,
  hovered: PanelId | null
): string {
  if (!hovered) {
    return panel === "left" || panel === "center"
      ? DIVIDER_VISIBLE
      : DIVIDER_HIDDEN;
  }
  if (hovered === "left") return panel === "left" ? DIVIDER_VISIBLE : DIVIDER_HIDDEN;
  if (hovered === "center") return panel === "center" ? DIVIDER_VISIBLE : DIVIDER_HIDDEN;
  return DIVIDER_HIDDEN;
}

function panelDividerLeftVisible(
  panel: PanelId,
  hovered: PanelId | null
): string {
  if (!hovered) return DIVIDER_HIDDEN;
  if (hovered === "center") return panel === "center" ? DIVIDER_VISIBLE : DIVIDER_HIDDEN;
  if (hovered === "right") return panel === "right" ? DIVIDER_VISIBLE : DIVIDER_HIDDEN;
  return DIVIDER_HIDDEN;
}

const PANEL_OVERFLOW =
  "max-md:overflow-hidden md:overflow-visible";

const PANEL_CONTENT_STACK =
  "relative z-30 flex flex-col flex-1 w-full max-md:pt-0 md:pt-[70px] md:pb-8 md:min-h-0";

const DESKTOP_SECTION =
  "md:min-h-screen md:h-auto md:overflow-visible";

const DESKTOP_PANEL_HEIGHT = "md:min-h-screen md:h-auto";

const CARD_GRID =
  "grid max-md:grid-cols-1 grid-cols-3 gap-2.5 max-md:gap-1 md:gap-3 w-full max-md:mt-0 md:mt-4 mt-8 transition-all duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)]";

const MOBILE_EXPAND_BODY =
  "max-md:flex-none max-md:overflow-visible";

const MOBILE_CONTENT_WRAP =
  "text-center max-md:transform-none transform skew-x-[-10deg] z-20 transition-all duration-500 w-full max-w-[600px] max-md:max-w-none px-8 max-md:px-4 max-md:pt-3 max-md:pb-7 md:px-6 md:py-4 md:pb-6 md:mx-auto";

const DESKTOP_EXPAND_BODY =
  "md:flex md:flex-col md:w-full md:overflow-visible md:py-2";

const SKEW_OUTER = "max-md:transform-none transform skew-x-[-10deg]";
const SKEW_INNER = "max-md:transform-none transform skew-x-[10deg]";

const MOBILE_PRIMARY_CARD =
  "max-md:p-3 max-md:shadow-md max-md:transform-none";

const MOBILE_CTA_SKEW =
  "max-md:transform-none";

const PRIMARY_CARD_TITLE =
  "font-teko text-[2.5rem] max-md:text-lg md:text-[2rem] m-0 leading-none uppercase";

const DESKTOP_PRIMARY_CARD = "md:p-6";

const PRIMARY_CARD_SUB =
  "text-sm max-md:text-[10px] max-md:mt-0 max-md:leading-tight mt-1 opacity-80 font-manrope";

const MOBILE_TAGS = "max-md:hidden";

const MOBILE_CTA =
  "max-md:p-2 max-md:mt-0.5 max-md:text-xs";

const MOBILE_ACTION_ICON =
  "w-12 h-12 max-md:w-8 max-md:h-8 rounded-full bg-white/60 flex items-center justify-center border border-solid border-[rgba(0,0,0,0.12)] group-hover:bg-white transition-all duration-300";

const UTILITY_WRAP =
  "max-md:col-span-1 max-md:flex max-md:flex-row max-sm:flex-col max-md:gap-1.5 max-sm:gap-0.5 max-md:items-stretch md:contents";

const UTILITY_LINK =
  "group bg-white/95 backdrop-blur-sm p-6 px-5 max-md:p-1.5 max-md:px-2 max-md:flex-1 max-md:min-w-0 max-sm:flex-none max-sm:w-full max-md:transform-none max-md:flex-row max-md:items-center max-md:gap-2 max-md:shadow-none max-md:hover:translate-y-0 transform skew-x-[-10deg] cursor-pointer transition-all duration-300 flex flex-col items-center justify-start md:justify-center gap-4 md:gap-2 border border-solid border-[rgba(0,0,0,0.08)] shadow-[0_5px_15px_rgba(0,0,0,0.10)] hover:bg-text-obsidian hover:text-white hover:-translate-y-1 hover:border-text-obsidian";

const UTILITY_LINK_ALT =
  "group bg-white/95 backdrop-blur-sm p-6 px-5 max-md:p-1.5 max-md:px-2 max-md:flex-1 max-md:min-w-0 max-sm:flex-none max-sm:w-full max-md:transform-none max-md:flex-row max-md:items-center max-md:gap-2 max-md:shadow-none max-md:hover:translate-y-0 transform skew-x-[-10deg] cursor-pointer transition-all duration-300 flex flex-col items-center justify-start md:justify-center gap-4 md:gap-2 border border-solid border-[rgba(0,0,0,0.08)] shadow-[0_5px_15px_rgba(0,0,0,0.10)] hover:bg-brand-yellow hover:text-text-obsidian hover:-translate-y-1 hover:border-brand-yellow no-underline text-inherit";

const UTILITY_ICON =
  "w-11 h-11 max-md:w-6 max-md:h-6 rounded-full bg-brand-yellow/25 flex items-center justify-center max-md:transform-none transform skew-x-[10deg] group-hover:bg-brand-yellow transition-all duration-300 shrink-0";

const UTILITY_TITLE =
  "font-teko text-xl max-md:text-sm max-sm:text-xs uppercase max-md:whitespace-normal max-sm:whitespace-nowrap";

const UTILITY_SUB =
  "text-xs max-md:hidden font-manrope opacity-70";

const UTILITY_TEXT =
  "max-md:transform-none max-md:text-left max-md:flex-1 transform skew-x-[10deg] text-center";

function panelFlex(panel: PanelId, hovered: PanelId | null): string {
  if (!hovered) return "md:flex-1";
  if (hovered === panel) return "md:flex-[3]";
  return "md:flex-[0.5]";
}

function panelImageClass(active: boolean): string {
  return active
    ? "opacity-100 scale-100 grayscale-0"
    : "max-md:opacity-100 max-md:scale-100 max-md:grayscale-0 md:opacity-0 md:scale-[1.2] md:grayscale";
}

function PanelHeaderLabel({
  panel,
  barClass,
  titleClass,
  eyebrowClass,
}: {
  panel: PanelId;
  barClass: string;
  titleClass: string;
  eyebrowClass: string;
}): JSX.Element {
  const meta = PANEL_META[panel];

  return (
    <div className="flex items-center gap-3 min-w-0">
      <span className={`w-1 h-6 shrink-0 ${barClass}`} />
      <div className="text-left min-w-0">
        <p
          className={`font-manrope text-[9px] uppercase tracking-[0.2em] ${eyebrowClass}`}
        >
          {meta.eyebrow}
        </p>
        <h2
          className={`font-teko text-xl uppercase leading-none truncate ${titleClass}`}
        >
          {meta.label}
        </h2>
      </div>
    </div>
  );
}

function DesktopPanelHeader({
  panel,
  hovered,
  barClass,
}: {
  panel: PanelId;
  hovered: PanelId | null;
  barClass: string;
}): JSX.Element {
  const isActive = hovered === panel;
  const isDimmed = hovered !== null && !isActive;

  return (
    <div
      className={`relative z-30 w-full items-center px-4 py-3 shrink-0 transition-opacity duration-300 max-md:hidden ${
        isActive ? "md:hidden" : "md:flex"
      } ${isDimmed ? "opacity-55" : "opacity-100"}`}
    >
      <PanelHeaderLabel
        panel={panel}
        barClass={barClass}
        titleClass="text-text-obsidian"
        eyebrowClass="text-text-slate/70"
      />
    </div>
  );
}

function DesktopHoverTitle({
  panel,
  hovered,
}: {
  panel: PanelId;
  hovered: PanelId | null;
}): JSX.Element {
  const meta = PANEL_META[panel];
  const isActive = hovered === panel;

  return (
    <h1
      className={`hidden md:block font-teko uppercase text-center w-full leading-[0.9] transition-all duration-[800ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] ${
        isActive
          ? "opacity-100 mb-4 md:mb-3 text-4xl lg:text-5xl text-white drop-shadow-[0_10px_40px_rgba(0,0,0,0.8)]"
          : "opacity-0 h-0 mb-0 overflow-hidden pointer-events-none"
      }`}
    >
      {meta.label}
    </h1>
  );
}

function panelCardsClass(
  panel: PanelId,
  hovered: PanelId | null,
  mobileExpanded: PanelId | null
): string {
  const desktop =
    hovered === panel
      ? "md:opacity-100 md:translate-y-0"
      : "md:opacity-0 md:translate-y-[50px]";
  const mobile =
    mobileExpanded === panel
      ? "max-md:opacity-100 max-md:translate-y-0"
      : "max-md:hidden";
  return `${mobile} ${desktop}`;
}

function mobilePanelFlex(panel: PanelId, expanded: PanelId | null): string {
  const collapsedSize = "max-md:flex-1 max-md:min-h-[calc((100svh-7.5rem)/3)]";
  if (expanded === null) return collapsedSize;
  return expanded === panel
    ? "max-md:flex-none max-md:shrink-0"
    : collapsedSize;
}

function mobileSectionHeight(expanded: PanelId | null): string {
  return expanded === null
    ? "max-md:h-[calc(100svh-7.5rem)]"
    : "max-md:h-auto max-md:min-h-[calc(100svh-7.5rem)]";
}

function MobilePanelHeader({
  panel,
  expanded,
  onToggle,
  barClass,
}: {
  panel: PanelId;
  expanded: PanelId | null;
  onToggle: () => void;
  barClass: string;
}): JSX.Element {
  const meta = PANEL_META[panel];
  const isOpen = expanded === panel;

  return (
    <button
      type="button"
      className="hidden max-md:flex relative z-30 w-full items-center justify-between gap-2 px-3 py-2 shrink-0"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      aria-expanded={isOpen}
      aria-label={`${isOpen ? "Collapse" : "Expand"} ${meta.label}`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <PanelHeaderLabel
          panel={panel}
          barClass={barClass}
          titleClass="text-white"
          eyebrowClass="text-white/70"
        />
      </div>
      <Icon
        name={isOpen ? "expand_less" : "expand_more"}
        size={22}
        className="text-white shrink-0"
      />
    </button>
  );
}

interface HeroProps {}

const BOROUGH_LINKS: { label: string; keyword: string }[] = [
  { label: "Kensington & Chelsea", keyword: "Kensington" },
  { label: "Westminster", keyword: "Westminster" },
  { label: "Camden", keyword: "Camden" },
  { label: "Islington", keyword: "Islington" },
  { label: "Hackney", keyword: "Hackney" },
  { label: "Tower Hamlets", keyword: "Tower Hamlets" },
  { label: "Southwark", keyword: "Southwark" },
  { label: "Hammersmith & Fulham", keyword: "Hammersmith" },
];

export default function Hero(props: HeroProps): JSX.Element {
  const [hoveredPanel, setHoveredPanel] = useState<
    "left" | "center" | "right" | null
  >(null);
  const [mobileExpanded, setMobileExpanded] = useState<PanelId | null>(null);
  const [postcodeSearch, setPostcodeSearch] = useState("");
  const navigate = useNavigate();

  const handlePostcodeSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = postcodeSearch.trim();
    if (!trimmed) {
      navigate("/services/rent/properties/list");
      return;
    }
    navigate(
      `/services/rent/properties/list?keyword=${encodeURIComponent(trimmed)}`
    );
  };

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("hero:hoveredPanel", { detail: { hoveredPanel } })
    );
  }, [hoveredPanel]);


  const setPanelHover = useCallback((panel: PanelId) => {
    if (window.matchMedia("(min-width: 768px)").matches) {
      setHoveredPanel(panel);
    }
  }, []);

  const clearPanelHover = useCallback(() => {
    if (window.matchMedia("(min-width: 768px)").matches) {
      setHoveredPanel(null);
    }
  }, []);

  const toggleMobilePanel = useCallback((panel: PanelId) => {
    setMobileExpanded((prev) => (prev === panel ? null : panel));
  }, []);

  const onPanelClick = useCallback(
    (to: string) => (e: React.MouseEvent) => {
      if (!window.matchMedia("(min-width: 768px)").matches) return;
      const target = e.target as HTMLElement | null;
      // Don't hijack clicks on actual links/buttons inside the panel.
      if (target?.closest("a")) return;
      navigate(to);
    },
    [navigate]
  );

  return (
    <>
      {/* Grid Background Layer */}
      <div className={styles.gridLayer} />

      <div className="relative w-full md:min-h-screen">
      {/* Mobile intro */}
      <div className="md:hidden relative z-20 px-6 pt-24 pb-2 text-center border-b border-solid border-[rgba(0,0,0,0.08)] bg-[rgba(255,255,255,0.65)] backdrop-blur-sm shrink-0">
        <h1 className="font-teko text-3xl max-md:text-[clamp(0.95rem,4.1vw,1.875rem)] uppercase leading-none text-text-obsidian tracking-wide max-md:whitespace-nowrap max-md:tracking-tight">
          Engineered for London Living
        </h1>
        <p className="font-manrope text-xs text-text-slate mt-2 max-w-md mx-auto">
          Tap a section to explore
        </p>
      </div>

      {/* Hero Section */}
      <section
        className={`relative w-full h-auto ${mobileSectionHeight(mobileExpanded)} max-md:min-h-[360px] max-md:flex max-md:flex-col ${DESKTOP_SECTION} flex flex-col md:flex-row md:items-stretch z-10`}
        onMouseLeave={clearPanelHover}
      >
        {/* Left Panel - Buy */}
        <div
          className={`${panelFlex("left", hoveredPanel)} ${mobilePanelFlex("left", mobileExpanded)} ${PANEL_OVERFLOW} ${DESKTOP_PANEL_HEIGHT} md:min-w-0 h-auto relative max-md:border-b max-md:border-b-solid max-md:border-b-brand-yellow transition-[flex] duration-[500ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] flex flex-col max-md:justify-start md:justify-start items-stretch md:items-center max-md:py-0 max-md:px-0 md:px-4 ${
            hoveredPanel === "left"
              ? "md:min-h-0 md:bg-transparent md:backdrop-blur-none"
              : "md:bg-[rgba(255,255,255,0.2)] md:backdrop-blur-[5px]"
          }`}
          onMouseEnter={() => setPanelHover("left")}
          onClick={onPanelClick("/services/buy")}
          role="presentation"
        >
          <img
            src={backgroundImg.livingRoom3}
            alt="Buy"
            className={`absolute top-0 left-0 w-full h-full object-cover transition-all duration-[800ms] z-[-1] ${panelImageClass(
              hoveredPanel === "left"
            )}`}
          />
          <div
            className={`absolute inset-0 bg-black/30 z-0 ${
              mobileExpanded === "left" ? "max-md:block" : "max-md:block max-md:opacity-80"
            } ${hoveredPanel === "left" ? "md:block" : "md:hidden"}`}
          />

          <div className={PANEL_CONTENT_STACK}>
          <DesktopPanelHeader
            panel="left"
            hovered={hoveredPanel}
            barClass="bg-text-obsidian"
          />

          <MobilePanelHeader
            panel="left"
            expanded={mobileExpanded}
            onToggle={() => toggleMobilePanel("left")}
            barClass="bg-text-obsidian"
          />

          <div
            className={`${MOBILE_EXPAND_BODY} ${
              mobileExpanded === "left" ? "max-md:block" : "max-md:hidden"
            } ${DESKTOP_EXPAND_BODY} w-full`}
          >
          <div className={MOBILE_CONTENT_WRAP}>
            <div className={`${SKEW_INNER} w-full flex flex-col items-center`}>
              <DesktopHoverTitle panel="left" hovered={hoveredPanel} />
              <div
                className={`${CARD_GRID} ${panelCardsClass("left", hoveredPanel, mobileExpanded)}`}
              >
                <NavLink
                  to="/services/buy"
                  className={`col-span-3 max-md:col-span-1 group bg-brand-yellow text-text-obsidian p-7 md:p-8 ${DESKTOP_PRIMARY_CARD} ${MOBILE_PRIMARY_CARD} ${SKEW_OUTER} cursor-pointer relative overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.2)] transition-all duration-300 hover:scale-[1.02] max-md:hover:scale-100 hover:shadow-[0_15px_40px_rgba(255,196,0,0.3)] ${
                    hoveredPanel === "left" ? "delay-100" : "delay-0"
                  }`}
                >
                  <div className={`${SKEW_INNER} flex items-start justify-between gap-6 max-md:gap-3`}>
                    <div>
                      <h3 className={PRIMARY_CARD_TITLE}>
                        Browse Sales
                      </h3>
                      <p className={PRIMARY_CARD_SUB}>
                        Exclusive homes via NVRE
                      </p>
                    </div>
                    <div className="shrink-0">
                      <div className={MOBILE_ACTION_ICON}>
                        <Icon name="arrow_forward" size={24} />
                      </div>
                    </div>
                  </div>
                  <div className={`${SKEW_INNER} mt-5 max-md:mt-2 flex flex-wrap gap-2 ${MOBILE_TAGS}`}>
                    <span className="text-xs font-manrope uppercase tracking-wider bg-white/55 px-3 py-1 border border-solid border-[rgba(0,0,0,0.12)]">
                      Sales Listings
                    </span>
                    <span className="text-xs font-manrope uppercase tracking-wider bg-white/55 px-3 py-1 border border-solid border-[rgba(0,0,0,0.12)]">
                      Off-Market
                    </span>
                    <span className="text-xs font-manrope uppercase tracking-wider bg-white/55 px-3 py-1 border border-solid border-[rgba(0,0,0,0.12)]">
                      nvre.co.uk
                    </span>
                  </div>
                </NavLink>

                <div className={UTILITY_WRAP}>
                <NavLink
                  to="/services/stamp-duty-calculator?language=english"
                  className={`${UTILITY_LINK} ${
                    hoveredPanel === "left" ? "delay-200" : "delay-0"
                  }`}
                >
                  <div className={UTILITY_ICON}>
                    <Icon name="gavel" size={20} />
                  </div>
                  <div className={UTILITY_TEXT}>
                    <div className={UTILITY_TITLE}>
                      Stamp Duty
                    </div>
                    <div className={UTILITY_SUB}>
                      Purchase tax calculator
                    </div>
                  </div>
                </NavLink>

                <NavLink
                  to="/services/mortgage-calculator"
                  className={`${UTILITY_LINK} ${
                    hoveredPanel === "left" ? "delay-300" : "delay-0"
                  }`}
                >
                  <div className={UTILITY_ICON}>
                    <Icon name="home" size={20} />
                  </div>
                  <div className={UTILITY_TEXT}>
                    <div className={UTILITY_TITLE}>
                      Mortgage
                    </div>
                    <div className={UTILITY_SUB}>
                      Finance & repayments
                    </div>
                  </div>
                </NavLink>

                <NavLink
                  to="/guides/areas"
                  className={`${UTILITY_LINK_ALT} ${
                    hoveredPanel === "left" ? "delay-500" : "delay-0"
                  }`}
                >
                  <div className={UTILITY_ICON}>
                    <Icon name="location_on" size={20} />
                  </div>
                  <div className={UTILITY_TEXT}>
                    <div className={UTILITY_TITLE}>
                      Area Guides
                    </div>
                    <div className={UTILITY_SUB}>
                      Explore London neighbourhoods
                    </div>
                  </div>
                </NavLink>
                </div>

                <a
                  href="https://nvre.co.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`col-span-3 max-md:col-span-1 border-2 border-solid border-brand-yellow text-brand-yellow p-4 ${MOBILE_CTA} ${MOBILE_CTA_SKEW} ${SKEW_OUTER} cursor-pointer transition-all duration-300 mt-2.5 flex items-center justify-center gap-2.5 hover:bg-brand-yellow hover:text-text-obsidian no-underline ${
                    hoveredPanel === "left" ? "delay-700" : "delay-0"
                  }`}
                >
                  <Icon name="open_in_new" size={24} className={`max-md:w-4 max-md:h-4 ${SKEW_INNER}`} />
                  <span className={`font-manrope font-bold uppercase tracking-[2px] text-sm max-md:text-xs ${SKEW_INNER}`}>
                    Visit nvre.co.uk
                  </span>
                </a>
              </div>
            </div>
          </div>
          </div>
          </div>
          <div
            className={`${PANEL_DIVIDER_RIGHT} ${DIVIDER_YELLOW} ${panelDividerRightVisible(
              "left",
              hoveredPanel
            )}`}
          />
        </div>

        {/* Center Panel - Renters */}
        <div
          className={`${panelFlex("center", hoveredPanel)} ${mobilePanelFlex("center", mobileExpanded)} ${PANEL_OVERFLOW} ${DESKTOP_PANEL_HEIGHT} md:min-w-0 h-auto relative max-md:border-b max-md:border-b-solid max-md:border-b-text-obsidian transition-[flex] duration-[500ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] flex flex-col max-md:justify-start md:justify-start items-stretch md:items-center max-md:py-0 max-md:px-0 md:px-4 ${
            hoveredPanel === "center"
              ? "md:min-h-0 md:bg-transparent md:backdrop-blur-none"
              : "md:bg-[rgba(255,255,255,0.2)] md:backdrop-blur-[5px]"
          }`}
          onMouseEnter={() => setPanelHover("center")}
          onClick={onPanelClick("/services/rent")}
          role="presentation"
        >
          <img
            src={backgroundImg.livingRoom2}
            alt="Renters"
            className={`absolute top-0 left-0 w-full h-full object-cover transition-all duration-[800ms] z-[-1] ${panelImageClass(
              hoveredPanel === "center"
            )}`}
          />
          <div
            className={`absolute inset-0 bg-black/30 z-0 ${
              mobileExpanded === "center" ? "max-md:block" : "max-md:block max-md:opacity-80"
            } ${hoveredPanel === "center" ? "md:block" : "md:hidden"}`}
          />

          <div className={PANEL_CONTENT_STACK}>
          <DesktopPanelHeader
            panel="center"
            hovered={hoveredPanel}
            barClass="bg-brand-yellow"
          />

          <MobilePanelHeader
            panel="center"
            expanded={mobileExpanded}
            onToggle={() => toggleMobilePanel("center")}
            barClass="bg-brand-yellow"
          />

          <div
            className={`${MOBILE_EXPAND_BODY} ${
              mobileExpanded === "center" ? "max-md:block" : "max-md:hidden"
            } ${DESKTOP_EXPAND_BODY} w-full`}
          >
          <div className={MOBILE_CONTENT_WRAP}>
            <div className={`${SKEW_INNER} w-full flex flex-col items-center`}>
              <DesktopHoverTitle panel="center" hovered={hoveredPanel} />
              <div
                className={`${CARD_GRID} ${panelCardsClass("center", hoveredPanel, mobileExpanded)}`}
              >
                <form
                  onSubmit={handlePostcodeSearch}
                  className={`col-span-3 max-md:col-span-1 group bg-brand-yellow text-text-obsidian p-7 md:p-8 ${DESKTOP_PRIMARY_CARD} ${MOBILE_PRIMARY_CARD} ${SKEW_OUTER} relative overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.2)] transition-all duration-300 hover:scale-[1.02] max-md:hover:scale-100 hover:shadow-[0_15px_40px_rgba(255,196,0,0.3)] ${
                    hoveredPanel === "center" ? "delay-100" : "delay-0"
                  }`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className={`${SKEW_INNER} flex items-start justify-between gap-6 max-md:gap-3`}>
                    <div>
                      <h3 className={PRIMARY_CARD_TITLE}>
                        Start Search
                      </h3>
                      <p className={PRIMARY_CARD_SUB}>
                        Browse live listings in London
                      </p>
                    </div>
                    <button
                      type="submit"
                      className="shrink-0 border-none bg-transparent p-0 cursor-pointer"
                      aria-label="Search properties"
                    >
                      <div className={MOBILE_ACTION_ICON}>
                        <Icon name="arrow_forward" size={24} />
                      </div>
                    </button>
                  </div>
                  <div className={`${SKEW_INNER} mt-5 max-md:mt-2 md:mt-3`}>
                    <input
                      type="text"
                      value={postcodeSearch}
                      onChange={(e) => setPostcodeSearch(e.target.value)}
                      placeholder="Postcode or area, e.g. SW3, Kensington"
                      className="w-full px-4 py-3 max-md:px-3 max-md:py-2 md:py-2.5 max-md:text-xs bg-white/80 border border-solid border-[rgba(0,0,0,0.12)] font-manrope text-sm text-text-obsidian focus:outline-none focus:border-text-obsidian placeholder:text-text-slate/70"
                      aria-label="Postcode or area search"
                    />
                  </div>
                  <div className={`${SKEW_INNER} mt-3 max-md:mt-1 flex flex-wrap gap-2 ${MOBILE_TAGS}`}>
                    <span className="text-xs font-manrope uppercase tracking-wider bg-white/55 px-3 py-1 border border-solid border-[rgba(0,0,0,0.12)]">
                      Map + List
                    </span>
                    <span className="text-xs font-manrope uppercase tracking-wider bg-white/55 px-3 py-1 border border-solid border-[rgba(0,0,0,0.12)]">
                      Filters
                    </span>
                    <span className="text-xs font-manrope uppercase tracking-wider bg-white/55 px-3 py-1 border border-solid border-[rgba(0,0,0,0.12)]">
                      Live Availability
                    </span>
                  </div>
                </form>

                <div className={UTILITY_WRAP}>
                <NavLink
                  to="/services/rent"
                  className={`${UTILITY_LINK} ${
                    hoveredPanel === "center" ? "delay-200" : "delay-0"
                  }`}
                >
                  <div className={UTILITY_ICON}>
                    <Icon name="menu_book" size={20} />
                  </div>
                  <div className={UTILITY_TEXT}>
                    <div className={UTILITY_TITLE}>
                      Guides
                    </div>
                    <div className={UTILITY_SUB}>
                      Renting steps & FAQs
                    </div>
                  </div>
                </NavLink>

                <NavLink
                  to="/about-us#regulatory-stack"
                  className={`${UTILITY_LINK} ${
                    hoveredPanel === "center" ? "delay-300" : "delay-0"
                  }`}
                >
                  <div className={UTILITY_ICON}>
                    <Icon name="gavel" size={20} />
                  </div>
                  <div className={UTILITY_TEXT}>
                    <div className={UTILITY_TITLE}>
                      Compliance
                    </div>
                    <div className={UTILITY_SUB}>
                      Agency standards & regulations
                    </div>
                  </div>
                </NavLink>

                <NavLink
                  to="/guides/areas"
                  className={`${UTILITY_LINK_ALT} ${
                    hoveredPanel === "center" ? "delay-500" : "delay-0"
                  }`}
                >
                  <div className={UTILITY_ICON}>
                    <Icon name="location_on" size={20} />
                  </div>
                  <div className={UTILITY_TEXT}>
                    <div className={UTILITY_TITLE}>
                      Area Guides
                    </div>
                    <div className={UTILITY_SUB}>
                      Explore top London areas
                    </div>
                  </div>
                </NavLink>
                </div>

                <NavLink
                  to="/contact-us"
                  className={`col-span-3 max-md:col-span-1 border-2 border-solid border-brand-yellow text-brand-yellow p-4 ${MOBILE_CTA} ${MOBILE_CTA_SKEW} ${SKEW_OUTER} cursor-pointer transition-all duration-300 mt-2.5 flex items-center justify-center gap-2.5 hover:bg-brand-yellow hover:text-text-obsidian ${
                    hoveredPanel === "center" ? "delay-700" : "delay-0"
                  }`}
                >
                  <Icon name="call" size={24} className={`max-md:w-4 max-md:h-4 ${SKEW_INNER}`} />
                  <span className={`font-manrope font-bold uppercase tracking-[2px] text-sm max-md:text-xs ${SKEW_INNER}`}>
                    Speak to Us
                  </span>
                </NavLink>
              </div>
            </div>
          </div>
          </div>
          </div>
          <div
            className={`${PANEL_DIVIDER_LEFT} ${DIVIDER_YELLOW} ${panelDividerLeftVisible(
              "center",
              hoveredPanel
            )}`}
          />
          <div
            className={`${PANEL_DIVIDER_RIGHT} ${DIVIDER_OBSIDIAN} ${panelDividerRightVisible(
              "center",
              hoveredPanel
            )}`}
          />
        </div>

        {/* Right Panel - Landlords */}
        <div
          className={`${panelFlex("right", hoveredPanel)} ${mobilePanelFlex("right", mobileExpanded)} ${PANEL_OVERFLOW} ${DESKTOP_PANEL_HEIGHT} md:min-w-0 h-auto relative transition-[flex] duration-[500ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] flex flex-col max-md:justify-start md:justify-start items-stretch md:items-center max-md:py-0 max-md:px-0 md:px-4 ${
            hoveredPanel === "right"
              ? "md:min-h-0 md:bg-transparent md:backdrop-blur-none"
              : "md:bg-[rgba(255,255,255,0.2)] md:backdrop-blur-[5px]"
          }`}
          onMouseEnter={() => setPanelHover("right")}
          onClick={onPanelClick("/services/landlords")}
          role="presentation"
        >
          <img
            src={backgroundImg.livingRoom5}
            alt="Landlords"
            className={`absolute top-0 left-0 w-full h-full object-cover transition-all duration-[800ms] z-[-1] ${panelImageClass(
              hoveredPanel === "right"
            )}`}
          />
          <div
            className={`absolute inset-0 bg-black/30 z-0 ${
              mobileExpanded === "right" ? "max-md:block" : "max-md:block max-md:opacity-80"
            } ${hoveredPanel === "right" ? "md:block" : "md:hidden"}`}
          />

          <div className={PANEL_CONTENT_STACK}>
          <DesktopPanelHeader
            panel="right"
            hovered={hoveredPanel}
            barClass="bg-brand-yellow"
          />

          <MobilePanelHeader
            panel="right"
            expanded={mobileExpanded}
            onToggle={() => toggleMobilePanel("right")}
            barClass="bg-brand-yellow"
          />

          <div
            className={`${MOBILE_EXPAND_BODY} ${
              mobileExpanded === "right" ? "max-md:block" : "max-md:hidden"
            } ${DESKTOP_EXPAND_BODY} w-full`}
          >
          <div className={MOBILE_CONTENT_WRAP}>
            <div className={`${SKEW_INNER} w-full flex flex-col items-center`}>
              <DesktopHoverTitle panel="right" hovered={hoveredPanel} />
              <div
                className={`${CARD_GRID} ${panelCardsClass("right", hoveredPanel, mobileExpanded)}`}
              >
                {/* Primary Card - Valuation */}
                <NavLink
                  to="/services/rent-estimate"
                  className={`col-span-3 max-md:col-span-1 group bg-brand-yellow text-text-obsidian p-7 md:p-8 ${DESKTOP_PRIMARY_CARD} ${MOBILE_PRIMARY_CARD} ${SKEW_OUTER} cursor-pointer relative overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.2)] transition-all duration-300 hover:scale-[1.02] max-md:hover:scale-100 hover:shadow-[0_15px_40px_rgba(255,196,0,0.3)] ${
                    hoveredPanel === "right" ? "delay-100" : "delay-0"
                  }`}
                >
                  <div className={`${SKEW_INNER} flex items-start justify-between gap-6 max-md:gap-3`}>
                    <div>
                      <h3 className={PRIMARY_CARD_TITLE}>
                        Get Rent Estimate
                      </h3>
                      <p className={PRIMARY_CARD_SUB}>
                        Instant data-driven appraisal
                      </p>
                    </div>
                    <div className="shrink-0">
                      <div className={MOBILE_ACTION_ICON}>
                        <Icon name="arrow_forward" size={24} />
                      </div>
                    </div>
                  </div>
                  <div className={`${SKEW_INNER} mt-5 max-md:mt-2 flex flex-wrap gap-2 ${MOBILE_TAGS}`}>
                    <span className="text-xs font-manrope uppercase tracking-wider bg-white/55 px-3 py-1 border border-solid border-[rgba(0,0,0,0.12)]">
                      Rent Estimate
                    </span>
                    <span className="text-xs font-manrope uppercase tracking-wider bg-white/55 px-3 py-1 border border-solid border-[rgba(0,0,0,0.12)]">
                      Yield Tools
                    </span>
                    <span className="text-xs font-manrope uppercase tracking-wider bg-white/55 px-3 py-1 border border-solid border-[rgba(0,0,0,0.12)]">
                      Compliance
                    </span>
                  </div>
                </NavLink>

                <div className={UTILITY_WRAP}>
                <NavLink
                  to="/services/rental-yield"
                  className={`${UTILITY_LINK} ${
                    hoveredPanel === "right" ? "delay-200" : "delay-0"
                  }`}
                >
                  <div className={UTILITY_ICON}>
                    <Icon name="stacked_line_chart" size={20} />
                  </div>
                  <div className={UTILITY_TEXT}>
                    <div className={UTILITY_TITLE}>
                      Yield
                    </div>
                    <div className={UTILITY_SUB}>
                      ROI & cashflow tools
                    </div>
                  </div>
                </NavLink>

                <NavLink
                  to="/services/mortgage-calculator"
                  className={`${UTILITY_LINK} ${
                    hoveredPanel === "right" ? "delay-300" : "delay-0"
                  }`}
                >
                  <div className={UTILITY_ICON}>
                    <Icon name="home" size={20} />
                  </div>
                  <div className={UTILITY_TEXT}>
                    <div className={UTILITY_TITLE}>
                      Mortgage
                    </div>
                    <div className={UTILITY_SUB}>
                      Broker & finance tools
                    </div>
                  </div>
                </NavLink>

                <NavLink
                  to="/services/stamp-duty-calculator?language=english"
                  className={`${UTILITY_LINK} ${
                    hoveredPanel === "right" ? "delay-500" : "delay-0"
                  }`}
                >
                  <div className={UTILITY_ICON}>
                    <Icon name="gavel" size={20} />
                  </div>
                  <div className={UTILITY_TEXT}>
                    <div className={UTILITY_TITLE}>
                      Legal
                    </div>
                    <div className={UTILITY_SUB}>
                      Stamp duty calculator
                    </div>
                  </div>
                </NavLink>
                </div>

                {/* Secondary CTA */}
                <NavLink
                  to="/contact-us"
                  className={`col-span-3 max-md:col-span-1 border-2 border-solid border-brand-yellow text-brand-yellow p-4 ${MOBILE_CTA} ${MOBILE_CTA_SKEW} ${SKEW_OUTER} cursor-pointer transition-all duration-300 mt-2.5 flex items-center justify-center gap-2.5 hover:bg-brand-yellow hover:text-text-obsidian ${
                    hoveredPanel === "right" ? "delay-700" : "delay-0"
                  }`}
                >
                  <Icon name="calendar_month" size={24} className={`max-md:w-4 max-md:h-4 ${SKEW_INNER}`} />
                  <span className={`font-manrope font-bold uppercase tracking-[2px] text-sm max-md:text-xs ${SKEW_INNER}`}>
                    Talk to an Expert
                  </span>
                </NavLink>
              </div>
            </div>
          </div>
          </div>
          </div>
          <div
            className={`${PANEL_DIVIDER_LEFT} ${DIVIDER_OBSIDIAN} ${panelDividerLeftVisible(
              "right",
              hoveredPanel
            )}`}
          />
        </div>
      </section>

      {/* Slogan Container - Visible when idle on desktop */}
      <div
        className={`hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform skew-x-[-10deg] z-[15] pointer-events-none text-center w-[80%] transition-all duration-500 ${
          hoveredPanel
            ? "opacity-0 blur-[20px] scale-110"
            : "opacity-100 blur-0 scale-100"
        }`}
      >
        <div className="block transform skew-x-[10deg]">
          <h1 className="font-teko text-5xl md:text-[6vw] leading-[0.9] uppercase text-text-obsidian mb-4 tracking-wide drop-shadow-[0_10px_20px_rgba(255,255,255,0.8)]">
            Engineered for
            <br />
            London Living
          </h1>
          <p className="font-manrope text-lg text-[#444] max-w-[600px] mx-auto leading-relaxed font-light bg-[rgba(242,242,242,0.8)] p-2.5 rounded">
            A premier agency for luxury rentals and sales.
            <br />
            Seamless service. Prime locations.
          </p>
        </div>
      </div>

      {/* marquee footer */}
      <div className="relative md:absolute md:bottom-0 left-0 w-full bg-white border-y border-solid border-[rgba(0,0,0,0.1)] py-2 z-[50] overflow-hidden whitespace-nowrap">
        <div className={styles.track}>
          {[...BOROUGH_LINKS, ...BOROUGH_LINKS].map((borough, idx) => (
            <NavLink
              key={`${borough.label}-${idx}`}
              to={`/services/rent/properties/list?keyword=${encodeURIComponent(borough.keyword)}`}
              className={styles.tagLink}
              onClick={(e) => e.stopPropagation()}
            >
              <span>●</span> {borough.label}
            </NavLink>
          ))}
        </div>
      </div>
      </div>
    </>
  );
}
